import { db } from '../firebase/config';
import {
    collection,
    addDoc,
    query,
    where,
    getDocs,
    updateDoc,
    doc,
    serverTimestamp,
    runTransaction,
    orderBy,
    limit,
    getDoc
} from 'firebase/firestore';

export const CREDIT_TYPES = {
    PATH_COMPLETED: 'path_completed',
    LEVEL_CLEARED: 'level_cleared',
    COURSE_COMPLETED: 'course_completed',
    TASK_COMPLETED: 'task_completed',
    QUIZ_COMPLETED: 'quiz_completed',
    ADMIN_ASSIGNED: 'admin_assigned',
    COMPETITION_WON: 'competition_won',
    TEAM_BONUS: 'team_bonus',
    VOLUNTEERING: 'volunteering',
    PENALTY: 'penalty'
};

export const CREDIT_AMOUNTS = {
    [CREDIT_TYPES.PATH_COMPLETED]: 100,
    [CREDIT_TYPES.LEVEL_CLEARED]: 50,
    [CREDIT_TYPES.COURSE_COMPLETED]: 100,
    [CREDIT_TYPES.TASK_COMPLETED]: 25,
    [CREDIT_TYPES.QUIZ_COMPLETED]: 20,
    [CREDIT_TYPES.COMPETITION_WON]: 300,
    [CREDIT_TYPES.TEAM_BONUS]: 50,
    [CREDIT_TYPES.VOLUNTEERING]: 75
};

class CreditService {
    // Spend credits to unlock dashboard
    async spendCreditsForUnlock(userId, required = 480) {
        try {
            await runTransaction(db, async (transaction) => {
                const userRef = doc(db, 'users', userId);
                const userDoc = await transaction.get(userRef);
                if (!userDoc.exists()) {
                    throw new Error('User not found');
                }
                const currentCredits = userDoc.data()?.totalCredits || 0;
                if (currentCredits < required) {
                    throw new Error('Insufficient credits to unlock dashboard');
                }
                if (userDoc.data()?.dashboardUnlocked) {
                    throw new Error('Dashboard already unlocked');
                }

                // Create spend record
                const creditRef = doc(collection(db, 'credits'));
                transaction.set(creditRef, {
                    assignedBy: userId,
                    assignedTo: userId,
                    amount: -Math.abs(required),
                    activityType: 'dashboard_unlock',
                    description: `Spent ${required} credits to unlock dashboard`,
                    status: 'approved',
                    createdAt: serverTimestamp(),
                    metadata: { source: 'user', type: 'deduction' }
                });

                // Update user
                transaction.update(userRef, {
                    totalCredits: currentCredits - Math.abs(required),
                    dashboardUnlocked: true,
                    dashboardUnlockedAt: serverTimestamp(),
                    lastCreditUpdate: serverTimestamp()
                });
            });
            return { success: true };
        } catch (error) {
            console.error('Error spending credits for unlock:', error);
            throw error;
        }
    }

    // Award credits automatically when activities are completed
    async awardAutoCredits(userId, activityType, activityId, amount = null) {
        try {
            // Check for duplicates
            const creditsRef = collection(db, 'credits');
            const duplicateQuery = query(
                creditsRef,
                where('assignedTo', '==', userId),
                where('activityType', '==', activityType),
                where('metadata.activityId', '==', activityId)
            );

            const existingCredits = await getDocs(duplicateQuery);
            if (!existingCredits.empty) {
                console.log('Credits already awarded for this activity');
                return { success: false, message: 'Credits already awarded' };
            }

            // Determine credit amount
            const creditAmount = amount || CREDIT_AMOUNTS[activityType] || 0;

            // Create credit transaction
            await runTransaction(db, async (transaction) => {
                // Read first
                const userRef = doc(db, 'users', userId);
                const userDoc = await transaction.get(userRef);
                const currentCredits = userDoc.data()?.totalCredits || 0;

                // Create credit document (writes after reads)
                const creditRef = doc(collection(db, 'credits'));
                transaction.set(creditRef, {
                    assignedBy: 'SYSTEM',
                    assignedTo: userId,
                    amount: creditAmount,
                    activityType,
                    description: `Earned ${creditAmount} credits for ${activityType}`,
                    status: 'approved',
                    createdAt: serverTimestamp(),
                    metadata: {
                        activityId,
                        source: 'automated'
                    }
                });

                // Update user's total credits
                transaction.update(userRef, {
                    totalCredits: currentCredits + creditAmount,
                    lastCreditUpdate: serverTimestamp()
                });
            });

            return {
                success: true,
                amount: creditAmount,
                message: `${creditAmount} credits awarded successfully`
            };
        } catch (error) {
            console.error('Error awarding auto credits:', error);
            throw error;
        }
    }

    // Assign credits manually by admin
    async assignCredit({ assignedTo, userId, amount, activityType, description, assignedBy }) {
        try {
            const targetUser = assignedTo || userId;
            if (!targetUser) {
                throw new Error('assignedTo is required');
            }
            await runTransaction(db, async (transaction) => {
                // Read first
                const userRef = doc(db, 'users', targetUser);
                const userDoc = await transaction.get(userRef);
                const currentCredits = userDoc.data()?.totalCredits || 0;

                // Create credit document (writes after reads)
                const creditRef = doc(collection(db, 'credits'));
                transaction.set(creditRef, {
                    assignedBy,
                    assignedTo: targetUser,
                    amount: parseInt(amount),
                    activityType: activityType || CREDIT_TYPES.ADMIN_ASSIGNED,
                    description: description || 'Admin assigned credits',
                    status: 'approved',
                    createdAt: serverTimestamp(),
                    metadata: {
                        source: 'manual'
                    }
                });

                // Update user's total credits
                transaction.update(userRef, {
                    totalCredits: currentCredits + parseInt(amount),
                    lastCreditUpdate: serverTimestamp()
                });
            });

            return { success: true, message: 'Credits assigned successfully' };
        } catch (error) {
            console.error('Error assigning credits:', error);
            throw error;
        }
    }

    // Assign credits to a team (distributes to all members)
    async assignTeamCredits({ teamId, amount, activityType, description, assignedBy }) {
        try {
            // Get team members
            const teamRef = doc(db, 'teams', teamId);
            const teamDoc = await getDoc(teamRef);

            if (!teamDoc.exists()) {
                throw new Error('Team not found');
            }

            const members = teamDoc.data().members || [];
            const results = { success: [], failed: [] };

            // Assign credits to each member
            for (const memberId of members) {
                try {
                    await this.assignCredit({
                        assignedTo: memberId,
                        amount,
                        activityType: activityType || CREDIT_TYPES.TEAM_BONUS,
                        description: description || `Team bonus: ${teamDoc.data().name}`,
                        assignedBy
                    });
                    results.success.push(memberId);
                } catch (error) {
                    results.failed.push({ userId: memberId, error: error.message });
                }
            }

            return results;
        } catch (error) {
            console.error('Error assigning team credits:', error);
            throw error;
        }
    }

    // Deduct credits (penalty)
    async deductCredits(userId, amount, reason, adminId) {
        try {
            await runTransaction(db, async (transaction) => {
                // Read first
                const userRef = doc(db, 'users', userId);
                const userDoc = await transaction.get(userRef);
                const currentCredits = userDoc.data()?.totalCredits || 0;

                // Create negative credit document (writes after reads)
                const creditRef = doc(collection(db, 'credits'));
                transaction.set(creditRef, {
                    assignedBy: adminId,
                    assignedTo: userId,
                    amount: -Math.abs(amount),
                    activityType: CREDIT_TYPES.PENALTY,
                    description: reason || 'Credit penalty',
                    status: 'approved',
                    createdAt: serverTimestamp(),
                    metadata: {
                        source: 'manual',
                        type: 'deduction'
                    }
                });

                // Update user's total credits
                transaction.update(userRef, {
                    totalCredits: Math.max(0, currentCredits - Math.abs(amount)),
                    lastCreditUpdate: serverTimestamp()
                });
            });

            return { success: true, message: 'Credits deducted successfully' };
        } catch (error) {
            console.error('Error deducting credits:', error);
            throw error;
        }
    }

    // Get user's credit history
    async getUserCredits(userId, options = {}) {
        try {
            const creditsRef = collection(db, 'credits');
            let q = query(
                creditsRef,
                where('assignedTo', '==', userId),
                orderBy('createdAt', 'desc')
            );

            if (options.limit) {
                q = query(q, limit(options.limit));
            }

            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate()
            }));
        } catch (error) {
            console.error('Error getting user credits:', error);
            throw error;
        }
    }

    // Get all credits (admin)
    async getAllCredits(options = {}) {
        try {
            const creditsRef = collection(db, 'credits');
            let q = query(creditsRef, orderBy('createdAt', 'desc'));

            if (options.limit) {
                q = query(q, limit(options.limit));
            }

            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate()
            }));
        } catch (error) {
            console.error('Error getting all credits:', error);
            throw error;
        }
    }

    // Bulk assign credits from CSV
    async bulkAssignCredits(creditsArray, adminId) {
        const results = { success: [], failed: [] };

        for (const credit of creditsArray) {
            try {
                await this.assignCredit({
                    assignedTo: credit.userId,
                    amount: credit.amount,
                    activityType: credit.activityType,
                    description: credit.description,
                    assignedBy: adminId
                });
                results.success.push(credit.userId);
            } catch (error) {
                results.failed.push({
                    userId: credit.userId,
                    error: error.message
                });
            }
        }

        return results;
    }

    // Get credit statistics (admin)
    async getCreditStatistics() {
        try {
            const creditsRef = collection(db, 'credits');
            const snapshot = await getDocs(creditsRef);

            let totalCreditsIssued = 0;
            const userCredits = new Map();

            snapshot.docs.forEach(doc => {
                const data = doc.data();
                totalCreditsIssued += data.amount;

                const userId = data.assignedTo;
                userCredits.set(userId, (userCredits.get(userId) || 0) + data.amount);
            });

            const totalUsers = userCredits.size;
            const averagePerUser = totalUsers > 0 ? Math.round(totalCreditsIssued / totalUsers) : 0;

            return {
                totalCreditsIssued,
                totalUsers,
                averagePerUser,
                pendingApprovals: 0 // Can be implemented if approval workflow is needed
            };
        } catch (error) {
            console.error('Error getting credit statistics:', error);
            throw error;
        }
    }
}

const creditService = new CreditService();
export default creditService;
